import { styled } from '@mui/material/styles';
import { Box, Switch, TextField, Typography } from '@mui/material';

// ============================================
// Layout (피그마 CRM_센터관리_포인트관리 기준)
// ============================================
export const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  paddingTop: '20px',
});

// ============================================
// 포인트 적립 설정 타이틀 (제목 + 토글, 하단 구분선)
// ============================================
export const SectionTitleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '16px 0',
  borderBottom: '1px solid #d9d9d9',
});

export const SectionTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  color: '#333',
  whiteSpace: 'nowrap',
});

// 타이틀+톱니를 합친 설정 버튼 — 빈 배경에 테두리만
export const SettingTitleButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '9px 16px',
  fontSize: '16px',
  fontWeight: 600,
  color: '#333',
  whiteSpace: 'nowrap',
  background: 'transparent',
  border: '1px solid #C9CDD4',
  borderRadius: '10px',
  cursor: 'pointer',
  '& svg': { color: '#8A8F98' },
  '&:hover': {
    borderColor: '#12B5A5',
    color: '#0E9487',
    '& svg': { color: '#12B5A5' },
  },
});

export const TealSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#4ab3bc',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#4ab3bc',
    opacity: 0.5,
  },
});

// ============================================
// 설정 입력 행 (기본값 / 최소 사용 포인트 / 저장 / 요약 카드)
// ============================================
export const SettingsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '24px',
  width: '100%',
});

export const SettingsFieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

export const SettingsFieldLabel = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  whiteSpace: 'nowrap',
});

export const SettingsFieldInline = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// 저장 버튼을 입력창 하단 라인에 맞춰 정렬 (요약 카드 100px 기준 중앙 배치 보정)
// 이 화면에서는 버튼을 축소해 요약 카드 공간을 넓힌다 (CommonModalButton 기본 44px → 36px)
export const SaveButtonWrapper = styled(Box)({
  alignSelf: 'flex-end',
  marginBottom: '10px',
  '& button': {
    height: '36px',
    minWidth: '64px',
    padding: '0 16px',
    fontSize: '14px',
  },
});

// 요약 카드(총 적립/차감) 영역 확장 — SettingsRow 안에서 카드가 더 넓게 차지하도록
export const SummaryCardsStretch = styled(Box)({
  flex: '1 1 640px',
  minWidth: 0,
  display: 'flex',
});

// 피그마 240px(1920 기준) 비례 유지 — 좁은 화면에서는 12.5vw로 줄어 요약 카드 공간을 확보
export const StyledTextField = styled(TextField)({
  width: 'clamp(160px, 12.5vw, 240px)',
  '& .MuiOutlinedInput-root': {
    height: '44px',
    borderRadius: '4px',
    fontSize: '16px',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ab3bc' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ab3bc' },
  },
});

// 입력창 오른쪽의 단위 텍스트 (예: 원)
export const InputSuffix = styled('span')({
  color: '#333',
  fontSize: '16px',
  fontWeight: 500,
});

export const SelectBranchNotice = styled(Typography)({
  paddingBottom: '30px',
  marginTop: '30px',
  fontSize: '20px',
  fontWeight: 500,
});

// 헤더의 포인트 기능 사용 상태 표시 (설정은 모달에서)
// 타이틀 텍스트와 수직 중앙이 맞도록 inline-flex + line-height 1 로 고정
export const SettingStatusText = styled('span', {
  shouldForwardProp: prop => prop !== '$enabled',
})(({ $enabled }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  alignSelf: 'center',
  marginLeft: '4px',
  lineHeight: 1,
  fontSize: '15px',
  fontWeight: 700,
  padding: '7px 14px',
  borderRadius: '99px',
  color: $enabled ? '#0E9487' : '#8A8F98',
  background: $enabled ? '#E7F8F6' : '#F2F3F6',
}));

/* 그룹 포인트 일괄 지급 — 설정 버튼 줄 오른쪽에 붙는다. */
export const GroupGrantRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginLeft: 'auto',
});

export const GroupGrantSelect = styled('select')({
  height: '38px',
  minWidth: '180px',
  padding: '0 10px',
  fontSize: '14px',
  color: '#333',
  background: '#fff',
  border: '1px solid #C9CDD4',
  borderRadius: '10px',
  cursor: 'pointer',
});

export const GroupGrantButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '38px',
  padding: '0 16px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
  whiteSpace: 'nowrap',
  background: '#2C3E50',
  border: 'none',
  borderRadius: '10px',
  cursor: 'pointer',
  '&:disabled': {
    background: '#C9CDD4',
    cursor: 'default',
  },
});

/* 포인트 지급 모달의 대상 선택 줄 (지점 전체 / 회원 선택). */
export const TargetRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '12px 14px',
  marginBottom: '14px',
  background: '#F7F8F9',
  borderRadius: '10px',
});

export const TargetOption = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '14px',
  color: '#333',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const TargetRadio = styled(Box, {
  shouldForwardProp: prop => prop !== '$checked',
})(({ $checked }) => ({
  width: '14px',
  height: '14px',
  borderRadius: '50%',
  border: `1px solid ${$checked ? '#00838F' : '#C9CDD4'}`,
  boxShadow: $checked ? 'inset 0 0 0 3px #fff' : 'none',
  backgroundColor: $checked ? '#00838F' : '#fff',
  flexShrink: 0,
}));

export const TargetPickButton = styled('button')({
  marginLeft: 'auto',
  height: '30px',
  padding: '0 12px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#333',
  background: '#fff',
  border: '1px solid #C9CDD4',
  borderRadius: '8px',
  cursor: 'pointer',
  '&:disabled': { color: '#bbb', borderColor: '#E5E8EB', cursor: 'default' },
});
